import { type ReactNode, useEffect, useState } from "react";

import { Button } from "../Button/Button";
import { Dialog } from "../Dialog/Dialog";
import { Input } from "../Input/Input";
import {
  CONFIRM_DESTRUCTIVE_DEFAULT_CANCEL_LABEL,
  resolveStepOneConfirmLabel,
  subscribeToConfirmDestructive,
  type typeConfirmDestructiveRequest,
} from "./confirmDestructiveBus";
import {
  buildTypeToConfirmFieldLabel,
  buildTypeToConfirmTitle,
  isTypedIdentifierMatch,
} from "./typeToConfirmLogic";

export const ConfirmDestructiveHost = (): ReactNode => {
  const [request, setRequest] = useState<typeConfirmDestructiveRequest | null>(
    null,
  );
  const [step, setStep] = useState<"one" | "two">("one");
  const [typed, setTyped] = useState("");

  useEffect(
    () =>
      subscribeToConfirmDestructive((next) => {
        setRequest(next);
        setStep("one");
        setTyped("");
      }),
    [],
  );

  const finish = (isConfirmed: boolean) => {
    request?.resolve(isConfirmed);
    setRequest(null);
    setStep("one");
    setTyped("");
  };

  if (request === null) return null;

  const cancelLabel =
    request.cancelLabel ?? CONFIRM_DESTRUCTIVE_DEFAULT_CANCEL_LABEL;
  const typeToConfirm = request.typeToConfirm;
  const isStepTwo = step === "two" && typeToConfirm !== undefined;
  const isMatch = typeToConfirm
    ? isTypedIdentifierMatch({ typed, expected: typeToConfirm.value })
    : false;

  return (
    <Dialog
      open
      onOpenChange={(next) => {
        if (!next) finish(false);
      }}
      title={
        isStepTwo
          ? buildTypeToConfirmTitle({ label: typeToConfirm.label })
          : request.title
      }
      autoFocus={step === "two"}
      footer={
        isStepTwo ? (
          <>
            <Button
              variant="secondary"
              onClick={() => {
                finish(false);
              }}
            >
              {cancelLabel}
            </Button>
            <Button
              variant="destructive"
              disabled={!isMatch}
              onClick={() => {
                finish(true);
              }}
            >
              {request.confirmLabel}
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="secondary"
              onClick={() => {
                finish(false);
              }}
            >
              {cancelLabel}
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                if (request.typeToConfirm) {
                  setStep("two");
                  setTyped("");
                  return;
                }
                finish(true);
              }}
            >
              {resolveStepOneConfirmLabel(request)}
            </Button>
          </>
        )
      }
    >
      {isStepTwo ? (
        <>
          <p
            className="font-mono text-body-strong text-ink"
            data-testid="confirm-destructive-identifier"
          >
            {typeToConfirm.value}
          </p>
          <p className="text-caption text-ink-secondary">
            Type it exactly as it appears above.
          </p>
          <Input
            label={buildTypeToConfirmFieldLabel({ label: typeToConfirm.label })}
            value={typed}
            onChange={(event) => {
              setTyped(event.target.value);
            }}
            autoComplete="off"
            autoCapitalize="none"
            spellCheck={false}
            data-testid="confirm-destructive-input"
          />
        </>
      ) : (
        <p
          className="whitespace-pre-line text-body text-ink-secondary"
          data-testid="confirm-destructive-body"
        >
          {request.body}
        </p>
      )}
    </Dialog>
  );
};
